import React, { useEffect, useState, useRef } from "react";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import 'ag-grid-enterprise';
import ConfirmDeleteModal from "../confirmdeletemodal";
import api from '../../../config/axiosConfig';

const Users = () => {
    const [formData, setFormData] = useState({
        name: '',
        username: '',
        password: '',
        role: '',
    });
    const [rowData, setRowData] = useState([]); // User data for the grid
    const [isEditMode, setIsEditMode] = useState(false); 
    const [roles, setRoles] = useState([]); // Roles for the role dropdown
    const [showModal, setShowModal] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const gridRef = useRef();

    // Fetch users and roles
    const getData = async () => {
        try {
            const rolesResponse = await api.get('/roles'); 
            // console.log("user roles are:" , rolesResponse);
            const usersResponse = await api.get('/users'); 

            setRoles(rolesResponse.data);
            setRowData(usersResponse.data); // Update grid data
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    useEffect(() => {
        getData();
    }, []);

    // Grid column definitions
    const columnDefs = [
        { headerName: 'ID', field: 'id', sort: "desc" },
        { headerName: 'Name', field: 'name' },
        { headerName: 'Username', field: 'username' },
        { headerName: '', field: 'username' },
        { headerName: 'Role', field: 'role' },
        {
            field: "actions",
            headerName: "Actions",
            cellRenderer: (params) => (
                <Button
                    variant="danger"
                    className="btn-sm"
                    onClick={() => handleDelete(params.data)}
                >
                    <i className="bi bi-trash3"></i>
                </Button>
            ),
        },
    ];

    // Handle row selection in the grid
    const onRowSelected = (event) => {
        if (event.node.isSelected()) {
            setFormData(event.data);
            setIsEditMode(true);
        }
    };

    // Form input change handler
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    // Add or update user
    const handleAddData = async (e) => {
        e.preventDefault();
        if (formData.name && formData.username && formData.password && formData.role) {
            try {
                const method = isEditMode ? 'put' : 'post';
                const url = isEditMode ? `/users/${formData.id}` : '/users';
                await api({ method, url, data: formData });

                setFormData({ name: '', username: '', password: '', role: '' });
                setIsEditMode(false);
                getData(); // Refresh data after add or update
            } catch (error) {
                console.error("Error saving user:", error);
            }
        } else {
            alert("Please fill out all fields.");
        }
    };

    // Start a new entry
    const handleNewEntry = () => {
        setFormData({ name: '', username: '', password: '', role: '' });
        setIsEditMode(false);
    };

    // Trigger delete modal
    const handleDelete = (user) => {
        setSelectedRow(user);
        setShowModal(true);
    };

    // Confirm deletion
    const confirmDelete = async () => {
        if (selectedRow) {
            try {
                await api.delete(`/users/${selectedRow.id}`);
                setShowModal(false);
                getData(); // Refresh data after delete
            } catch (error) {
                console.error("Error deleting user:", error);
            }
        }
    };

    return (
        <>
            <Container fluid>
                <Row className="mt-4 h-100">
                    <h6 className="text-start">User List</h6>
                    <div className="ag-theme-alpine" style={{ height: 400, width: '100%' }}>
                        <AgGridReact
                            ref={gridRef}
                            rowData={rowData} // Provide rowData to the grid
                            columnDefs={columnDefs}
                            defaultColDef={{
                                sortable: true,
                                filter: true,
                                resizable: true,
                                floatingFilter: true,
                            }}
                            onRowSelected={onRowSelected}
                        />
                    </div>
                </Row>
                <Row className="mt-4 border shadow p-4">
                    <h6 className="text-start">{isEditMode ? "Edit User" : "Add User"}</h6>
                    <Form onSubmit={handleAddData}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        placeholder="Enter name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Username</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="username"
                                        placeholder="Enter username"
                                        value={formData.username}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password</Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        placeholder="Enter password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Role</Form.Label>
                                    <Form.Select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Select Role</option>
                                        {roles.map(role => (
                                            <option key={role.id} value={role.id}>{role.Rolename}</option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Button type="submit" variant="primary">
                            {isEditMode ? "Update" : "Add"}
                        </Button>
                        {isEditMode && (
                            <Button variant="secondary" className="ms-3" onClick={handleNewEntry}>
                                Create New Entry
                            </Button>
                        )}
                    </Form>
                </Row>
            </Container>
            <ConfirmDeleteModal
                show={showModal}
                handleClose={() => setShowModal(false)}
                handleConfirm={confirmDelete}
                title="Confirm Deletion"
                body={`Are you sure you want to delete ${selectedRow?.name}?`}
                confirmText="Delete"
            />
        </>
    );
};

export default Users;
